import React, { useEffect, useRef } from "react";

import { useReactFlow } from "@xyflow/react";
import { observer } from "mobx-react-lite";
import { useSearchParams } from "react-router-dom";

import { useFlowVersion } from "src/api/flowVersionQueries";
import { ParentNode } from "src/constants/NodeDataTypes";
import { useSchemas } from "src/datasets/utils";
import { toastActions } from "src/design-system/Toast/utils";
import { validateFlowNodeMapping } from "src/parentFlowNodes/utils";
import { URLKeys } from "src/router/SearchParams";
import { useNodeValidations } from "src/store/NodeValidations";
import { useGraphStore } from "src/store/StoreProvider";
import { isParentNode } from "src/utils/predicates";

// The known etag only moves when the child's schema changes, so remembering which one the user
// has opened is enough to show the notification once per schema change without writing to the version.
const seenEtagKey = (nodeId: string): string =>
  `childVersionSeenEtag:${nodeId}`;
const hasSeenChildEtag = (node: ParentNode): boolean => {
  try {
    const seen = localStorage.getItem(seenEtagKey(node.id));
    return (
      seen !== null && seen === (node.data.child_flow_version_known_etag ?? "")
    );
  } catch {
    return false;
  }
};
const markChildEtagSeen = (node: ParentNode): void => {
  try {
    localStorage.setItem(
      seenEtagKey(node.id),
      node.data.child_flow_version_known_etag ?? "",
    );
  } catch {
    // storage unavailable, the notification will show again on the next load
  }
};

const DispatchNotificationBase: React.FC<{
  node: ParentNode;
}> = ({ node }) => {
  const { setSelectedNode, nodesArray } = useGraphStore();
  const childVersion = useFlowVersion(node.data.child_flow_version_id ?? "");
  const notificationRef = useRef<string>("");
  const { setCenter } = useReactFlow();
  const addInvalidNode = useNodeValidations((state) => state.addInvalidNode);
  const removeInvalidNode = useNodeValidations(
    (state) => state.removeInvalidNode,
  );
  const childSchemas = useSchemas(childVersion.data);
  const [params] = useSearchParams();
  const selectedNodeId = params.get(URLKeys.SelectedNodeId);

  // adds to invalid nodes list if any errors with child flow
  useEffect(() => {
    if (
      node.data.is_stale &&
      validateFlowNodeMapping(childSchemas, node.data).length > 0
    ) {
      addInvalidNode(node.id);
    } else {
      removeInvalidNode(node.id);
    }
  }, [childSchemas, node.data, node.id, addInvalidNode, removeInvalidNode]);

  // opening the node counts as having reviewed the child change
  useEffect(() => {
    if (selectedNodeId === node.id) {
      markChildEtagSeen(node);
      toastActions.dismiss(node.id);
    }
  }, [selectedNodeId, node]);

  // for displaying notifications
  useEffect(() => {
    if (
      childVersion.data &&
      selectedNodeId !== node.id &&
      !notificationRef.current &&
      !hasSeenChildEtag(node)
    ) {
      const errors = validateFlowNodeMapping(childSchemas, node.data);
      const getNodeFE = (nodeId: string) =>
        nodesArray.find((n) => n.id === nodeId);
      if (errors.length > 0) {
        notificationRef.current = toastActions.default({
          id: node.id,
          title: `Update to ${node.data.label}`,
          description:
            "Review changes and remap fields to complete updated configuration",
          actionText: "View",
          duration: Infinity,
          onActionClick: () => {
            setSelectedNode(node.id);
            toastActions.dismiss(notificationRef.current);
            // since opening sidebar changes width of the canvas setTimeout is required
            setTimeout(() => {
              const nodeFE = getNodeFE(node.id);
              if (nodeFE) {
                setCenter(nodeFE.position.x, nodeFE.position.y, {
                  zoom: 1,
                });
              }
            });
          },
        });
      }
    }
  }, [
    node,
    childVersion.data,
    selectedNodeId,
    nodesArray,
    childSchemas,
    addInvalidNode,
    setCenter,
    setSelectedNode,
  ]);

  // child flow deleted
  useEffect(() => {
    if (
      !notificationRef.current &&
      childVersion.failureReason?.response?.status === 403
    ) {
      const getNodeFE = (nodeId: string) =>
        nodesArray.find((n) => n.id === nodeId);
      notificationRef.current = toastActions.default({
        id: node.id,
        title: `Action required on Decision Flow Node`,
        description: `${node.data.label} Decision Flow has been deleted. Select a new Decision Flow.`,
        actionText: "View",
        duration: Infinity,
        onActionClick: () => {
          setSelectedNode(node.id);
          toastActions.dismiss(notificationRef.current);
          // since opening sidebar changes width of the canvas setTimeout is required
          setTimeout(() => {
            const nodeFE = getNodeFE(node.id);
            if (nodeFE) {
              setCenter(nodeFE.position.x, nodeFE.position.y, {
                zoom: 1,
              });
            }
          });
        },
      });
    }
  }, [node, childVersion, nodesArray, setSelectedNode, setCenter]);

  return null;
};

const DispatchNotification = observer(DispatchNotificationBase);

export const ChildVersionChangeNotifications: React.FC = () => {
  const { nodesArray } = useGraphStore();

  return (
    <>
      {nodesArray.map((node) => {
        if (isParentNode(node)) {
          if (node.data.child_flow_version_id) {
            return <DispatchNotification key={node.id} node={node} />;
          }
          return null;
        }
        return null;
      })}
    </>
  );
};
